import type { MouseEvent, PropsWithChildren, RefObject } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';
import {
  ContextMenuContextProvider,
  useActiveProjectId,
  useCloseContextMenu,
  useOpenContextMenu,
} from '@frameio/shared/contexts';
import type { ProjectTreeItem } from '../types';

type NavigationMenuState = {
  targetItem: ProjectTreeItem | null;
  targetItemRef: RefObject<HTMLElement | null> | null;
};

type NavigationMenuValue = {
  projectId: string | null;
  setState: React.Dispatch<React.SetStateAction<NavigationMenuState>> | null;
  state: NavigationMenuState;
};

const initialState: NavigationMenuState = {
  targetItem: null,
  targetItemRef: null,
};

const NavigationMenuContext = createContext<NavigationMenuValue>({
  projectId: null,
  setState: null,
  state: initialState,
});

export const NavigationMenuContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [state, setState] = useState<NavigationMenuState>(initialState);
  const projectId = useActiveProjectId() ?? null;
  const value = useMemo<NavigationMenuValue>(
    () => ({
      projectId,
      setState,
      state,
    }),
    [projectId, state]
  );

  return (
    <NavigationMenuContext.Provider value={value}>
      <ContextMenuContextProvider<string>>
        <>{children}</>
      </ContextMenuContextProvider>
    </NavigationMenuContext.Provider>
  );
};

export const useNavigationMenuState = () =>
  useContextSelector(NavigationMenuContext, ({ state }) => state);

export const useOpenNavigationMenu = () => {
  const setState = useContextSelector(
    NavigationMenuContext,
    (value) => value.setState
  );
  const openContextMenu = useOpenContextMenu<ProjectTreeItem, HTMLElement>();

  return useCallback(
    (
      targetItem: ProjectTreeItem,
      targetItemRef: NavigationMenuState['targetItemRef'],
      event?: MouseEvent
    ) => {
      if (targetItemRef?.current) {
        openContextMenu(targetItem, targetItemRef.current, event);
      }

      setState?.((value) => ({
        ...value,
        targetItemRef,
      }));
    },
    [setState, openContextMenu]
  );
};

export const useCloseNavigationMenu = () => {
  const closeContextMenu = useCloseContextMenu();

  return useCallback(() => {
    closeContextMenu();
  }, [closeContextMenu]);
};

export const useIsRenamingItem = (item: ProjectTreeItem) =>
  useContextSelector(
    NavigationMenuContext,
    ({ state }) =>
      state.targetItem?.id === item.id && state.targetItem.type === item.type
  );

export const useSetRenameItem = () => {
  const setState = useContextSelector(
    NavigationMenuContext,
    (value) => value.setState
  );

  return useCallback(
    (targetItem: NavigationMenuState['targetItem']) => {
      setState?.((value) => ({
        ...value,
        targetItem,
      }));
    },
    [setState]
  );
};
